import { Badge, Box, Button, Heading, HStack, Text, VStack } from '@chakra-ui/react'
import React from 'react'
import { PessoaEncontrada } from '../api/lookup'
import { useEventConfig } from '../hooks/useEventConfig'

interface Props {
  pessoas: PessoaEncontrada[]
  documento: string
  onEscolher: (p: PessoaEncontrada) => void
  onCancelar: () => void
}

/**
 * Escolha entre participantes que compartilham o mesmo documento.
 *
 * Acontece quando a inscrição foi feita com o CNPJ da empresa — no SBAM, três pessoas
 * sob o CNPJ da GEAP e duas sob o da Unimed. A tela nunca escolhe sozinha: o crachá
 * carrega o QR que abre as sessões, e entregar o errado troca a identidade da pessoa
 * dentro do evento inteiro.
 */
const SelecaoPessoa: React.FC<Props> = ({ pessoas, documento, onEscolher, onCancelar }) => {
  const { config } = useEventConfig()
  const primaria = config.branding?.primaryColor || '#092D77'
  const secundaria = config.branding?.secondaryColor || '#38B7B4'

  return (
    <Box minH="100vh" bg={primaria} color="white" px={6} py={8}>
      <VStack spacing={2} mb={6} textAlign="center">
        <Heading fontSize={{ base: '2xl', md: '4xl' }}>Quem é você?</Heading>
        <Text fontSize={{ base: 'md', md: 'xl' }} opacity={0.9}>
          Encontramos <strong>{pessoas.length} pessoas</strong> cadastradas com o documento{' '}
          {documento}
        </Text>
        <Text fontSize={{ base: 'sm', md: 'md' }} opacity={0.75}>
          Toque no seu nome para imprimir a credencial.
        </Text>
      </VStack>

      <VStack spacing={3} w="100%" maxW="820px" mx="auto">
        {pessoas.map(p => (
          <Button
            key={p.registrationUid || p.credentialCode || p.nome}
            onClick={() => onEscolher(p)}
            w="100%"
            h="auto"
            py={5}
            px={6}
            bg="whiteAlpha.200"
            _hover={{ bg: 'whiteAlpha.300' }}
            _active={{ bg: secundaria, transform: 'scale(0.99)' }}
            justifyContent="flex-start"
            whiteSpace="normal"
            textAlign="left"
          >
            <VStack align="start" spacing={1} w="100%">
              <HStack w="100%" justify="space-between" align="start">
                <Text fontSize={{ base: 'xl', md: '3xl' }} fontWeight="bold">
                  {p.nome}
                </Text>
                {p.jaCredenciado && (
                  <Badge colorScheme="green" fontSize={{ base: 'xs', md: 'sm' }}>
                    já credenciado
                  </Badge>
                )}
              </HStack>
              {/* Discriminadores: sem eles a escolha entre homônimos é adivinhação. */}
              <Text fontSize={{ base: 'sm', md: 'lg' }} opacity={0.85}>
                {[p.empresa, p.conselhoProfissional, p.estadoUf].filter(Boolean).join(' · ') ||
                  'Sem dados adicionais'}
              </Text>
            </VStack>
          </Button>
        ))}
      </VStack>

      <Box textAlign="center" mt={8}>
        <Button
          onClick={onCancelar}
          variant="outline"
          color="white"
          borderColor="whiteAlpha.600"
          _hover={{ bg: 'whiteAlpha.200' }}
          size="lg"
          px={10}
        >
          Nenhum destes — voltar
        </Button>
      </Box>
    </Box>
  )
}

export default SelecaoPessoa
